import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Loader } from '../../components/Loader/Loader';
import { createAuthOConnection } from './initiateAuthO';
import { useLoginApi } from '../../apiHooks/apiHooks';
import { AppContextData } from '../AppContext';

export const Auth0ProviderProxy = (authOProps) => {
  const { handleAuthLoginApi } = useLoginApi();
  const { state } = useContext(AppContextData);
  const [isAuthenticated, setIsAuthenticated] = useState();
  const [loading, setLoading] = useState(true);

  // React 18 fix: Prevent double-execution in Strict Mode
  const isInitialized = useRef(false);

  // Move authenticateToken outside of useEffect to avoid closure issues
  const authenticateToken = useCallback(
    // eslint-disable-next-line max-statements
    async (auth0FromHook) => {
      if (authOProps?.state?.state && authOProps?.state?.code) {
        // eslint-disable-next-line no-console
        console.info('[OAuth] Handling redirect callback...');
        const params = new URLSearchParams(window.location.search);
        params.set('code', authOProps.state.code);
        params.set('state', authOProps.state.state);
        window.history.pushState(
          {},
          '',
          `${window.location.pathname}?${params.toString()}`
        );
        await auth0FromHook.handleRedirectCallback();
      }
      // eslint-disable-next-line no-console
      console.info('[OAuth] Getting access token...');
      const accessToken = await auth0FromHook.getTokenSilently();
      if (accessToken) {
        // eslint-disable-next-line no-console
        console.info('[OAuth] Token received, authenticating...');
        const idToken = await auth0FromHook.getIdTokenClaims();
        await handleAuthLoginApi(state, idToken, auth0FromHook, accessToken);
        return true;
      }
      // eslint-disable-next-line no-console
      console.warn('[OAuth] No access token received');
      return null;
    },
    [
      authOProps?.state?.state,
      authOProps?.state?.code,
      handleAuthLoginApi,
      state
    ]
  );

  useEffect(() => {
    // eslint-disable-next-line max-len
    // React 18 Strict Mode runs effects twice - prevent double OAuth initialization
    if (isInitialized.current) {
      // eslint-disable-next-line no-console
      console.info('[OAuth] Already initialized, skipping');
      return;
    }
    isInitialized.current = true;

    // eslint-disable-next-line no-console
    console.info('[OAuth] AuthOwrapper initializing:', {
      provider: authOProps?.authType?.provider,
      clientId: authOProps?.authType?.clientId
        ? '✓ present'
        : '✗ missing',
      redirectUri: authOProps?.redirect_uri,
      hasCallbackCode: Boolean(authOProps?.state?.code)
    });

    let isCancelled = false;

    // eslint-disable-next-line max-statements, complexity
    (async function initiateAuthOLogin () {
      let url = new URL(window.location);
      let auth0FromHook;
      try {
        // Validate redirect URI matches current host
        if (authOProps?.redirect_uri.startsWith('https://')) {
          url = new URL(authOProps.redirect_uri);
          if (url.hostname !== window.location.host) {
            throw new Error(
              'Redirect URI not same as current host: current host: ' +
                window.location.host +
                ' redirect URI: ' +
                authOProps.redirect_uri
            );
          }
        } else if (authOProps?.redirect_uri !== window.location.host) {
          throw new Error(
            'Redirect URI not same as current host: current host: ' +
              window.location.host +
              ' redirect URI: ' +
              authOProps.redirect_uri
          );
        }

        // eslint-disable-next-line no-console
        console.info('[OAuth] Creating Auth0 connection...');

        // Create Auth0 connection
        auth0FromHook = await createAuthOConnection(authOProps.authType);

        if (isCancelled) {
          return;
        }

        // Handle OAuth callback with code and state
        if (
          authOProps.state &&
          authOProps.state.code &&
          authOProps.state.state
        ) {
          // eslint-disable-next-line no-console
          console.info('[OAuth] Processing callback with auth code');
          // Process callback and authenticate
          await authenticateToken(auth0FromHook);
          return;
        }

        // eslint-disable-next-line no-console
        console.info('[OAuth] Checking authentication status...');

        // Check if already authenticated
        const isAuthenticatedResult = await auth0FromHook.isAuthenticated();

        if (isCancelled) {
          return;
        }

        // eslint-disable-next-line no-console
        console.info(
          '[OAuth] Authentication status:',
          isAuthenticatedResult ? 'authenticated' : 'not authenticated'
        );

        setIsAuthenticated(isAuthenticatedResult);

        if (isAuthenticatedResult) {
          // eslint-disable-next-line no-console
          console.info('[OAuth] Already authenticated, getting token...');
          // Authenticate with existing token
          await authenticateToken(auth0FromHook);
        } else {
          // eslint-disable-next-line no-console
          console.info(
            '[OAuth] Redirecting to OAuth provider:',
            authOProps?.authType?.provider
          );

          // Redirect to OAuth provider (Okta)
          // v1.x SDK automatically includes client_id from config
          await auth0FromHook.loginWithRedirect();
        }
      } catch (e) {
        console.error('OAuth initialization error:', e);
        if (!isCancelled) {
          // Better error handling for debugging
          const errorMessage = e?.message || 'OAuth initialization failed';
          const errorCode = e?.error_code || 'OAUTH_INIT_ERROR';
          console.error('OAuth Error Details:', {
            message: errorMessage,
            code: errorCode,
            orgId: state.org_id,
            envId: state.env_id,
            redirectUri: authOProps.redirect_uri
          });

          // Check if this is a token exchange error (CORS/401)
          // Don't redirect if already processing callback - prevents loop
          const hasCallback =
            authOProps?.state?.code && authOProps?.state?.state;

          if (hasCallback && errorMessage.includes('fetch')) {
            // CORS or network error during token exchange
            // Direct redirect to error page (don't rely on OAuth logout)
            const corsError = encodeURIComponent(
              'OAuth token exchange failed. CORS/auth error. Contact support.'
            );
            window.location.href = window.location.origin +
              `/error?code=OAUTH_TOKEN_ERROR&message=${corsError}`;
          } else if (errorMessage && errorCode) {
            // Other OAuth errors - direct redirect to error page
            const encodedCode = encodeURIComponent(errorCode);
            const encodedMessage = encodeURIComponent(errorMessage);
            window.location.href = window.location.origin +
              `/error?code=${encodedCode}&message=${encodedMessage}`;
          } else {
            // Fallback redirect to login
            window.location.href =
              authOProps.redirect_uri +
              `?org=${state.org_id}&app=user&env=${state.env_id}`;
          }
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    })();

    // Cleanup function for React 18 Strict Mode
    // eslint-disable-next-line consistent-return
    return () => {
      isCancelled = true;
    };
  }, [authOProps, authenticateToken, state.org_id, state.env_id]);

  return (
    <div>
      {!isAuthenticated && loading ? <Loader type="fullScreen" /> : null}
    </div>
  );
};

export const Auth0Provider = Auth0ProviderProxy;
